/*
Origin Core 1.4

called from /snippets/scripts.liquid

SUMMARY
--------------------
Reveals [data-animate] elements as they scroll into view, using an IntersectionObserver
(no scroll/resize listeners).

Elements start at opacity:0 (see src/components/animations.css), so anything already inside the
initial viewport must be revealed as early as possible — otherwise it stays invisible until this
script runs and then fades in over 700ms, which delays LCP and reads as a flash on load. Those
elements are marked `data-animate-immediate` so the transition is suppressed for them.
*/

/**
 * Reveal an element, optionally skipping the transition.
 * @param {Element} el
 * @param {boolean} [immediate]
 */
function reveal(el, immediate) {
  if (immediate) el.setAttribute('data-animate-immediate', '');
  el.setAttribute('data-animated', '');
}

function initAnimation() {
  const elems = document.querySelectorAll('[data-animate]:not([data-animated])');

  if (!elems.length) return;

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  /** @type {Element[]} */
  const pending = [];

  for (const el of elems) {
    // Already within (or above) the initial viewport — show it now, with no transition.
    if (el.getBoundingClientRect().top < viewportHeight) {
      reveal(el, true);
    } else {
      pending.push(el);
    }
  }

  if (!pending.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          reveal(entry.target);
          observer.unobserve(entry.target);
        }
      });
    },
    // rootMargin '-10% bottom' mirrors the original `windowHeight * 0.90` threshold
    { threshold: 0, rootMargin: '0px 0px -10% 0px' }
  );

  pending.forEach((el) => observer.observe(el));
}

/**
 * Watch for [data-animate] elements added after the initial pass.
 *
 * These elements start at opacity:0 and are only ever revealed by this script, so anything
 * inserted later — the Section Rendering API, a theme-editor section reload, product
 * recommendations arriving from fetch — stays invisible permanently unless it gets observed.
 * The `animation:init` event below was the intended hook for that, but nothing in the theme ever
 * dispatched it. It is kept for manual use; this observer is what actually covers the case.
 *
 * Note also that `section.index0` (used by _container-settings to skip animating the first
 * section) returns nil under the Section Rendering API, so re-rendered sections can arrive
 * carrying data-animate even when the first-section guard would have excluded them.
 */
function watchForNewSections() {
  if (!document.body) return;

  let scheduled = false;

  const observer = new MutationObserver((mutations) => {
    if (scheduled) return;

    const hasCandidate = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (/** @type {Element} */ (node).matches?.('[data-animate]:not([data-animated])') ||
            /** @type {Element} */ (node).querySelector?.('[data-animate]:not([data-animated])'))
      )
    );

    if (!hasCandidate) return;

    // Coalesce bursts of mutations into a single pass.
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      initAnimation();
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

function start() {
  initAnimation();
  watchForNewSections();
}

// This file is loaded as a module, so it executes after parsing but before DOMContentLoaded —
// run straight away rather than waiting, to keep the above-the-fold reveal as early as possible.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}

document.addEventListener('animation:init', initAnimation);
